/**
 * Desktop and Template menus for Footer
 */

import React from "react";
import ReactDOM from "react-dom";
import { v4 as uuidv4 } from "uuid";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Divider } from "primereact/divider";
import { useChartsStore } from "~/store/chartStore";
import useWorkspaceStore from "~/store/workspaceStore";
import useWatchlistStore from "~/store/watchlistStore";
import useBookByPriceStore from "~/store/bookByPriceStore";
import { useSettingsStore } from "~/store/settingsStore";
import { isFeatureEnabled } from "~/data/Services/settings";
import {
  convertLegacyWorkspace,
  validateConvertedWorkspace,
  getConversionStats,
} from "~/workspace/storage/legacyConverter";
import { deserializeWorkspace } from "~/workspace/core/serialization";
import {
  exportWorkspace,
  importWorkspace,
  saveDesktopRemote,
  listDesktopsRemote,
  deleteDesktopRemote,
  renameDesktopRemote,
  duplicateDesktopRemote,
  desktopNameExists,
} from "../../workspace";
import {
  saveTemplateRemote,
  loadTemplateRemote,
  listTemplatesRemote,
  deleteTemplateRemote,
  renameTemplateRemote,
  duplicateTemplateRemote,
} from "../../workspace/storage/templateStorage";
import {
  exportTemplate,
  importTemplate,
} from "../../workspace/storage/templateFileStorage";
import {
  switchToDesktop,
  markDesktopSavedToServer,
  reloadDesktopFromServer,
} from "../../workspace/desktopManager";
import { clearDesktopCache } from "../../workspace/storage/desktopCache";
import { getWorkspaceContext } from "~/store/workspaceStore";
import { eventBus, EVENTS } from "~/utils/eventBus";
import { deserializeTemplate } from "../../workspace/core/templateSerialization";
import SaveDesktopDialog from "../../workspace/ui/SaveDesktopDialog";
import SaveAsDesktopDialog from "../../workspace/ui/SaveAsDesktopDialog";
import LoadDesktopDialog from "../../workspace/ui/LoadDesktopDialog";
import LoadLegacyDesktopDialog from "../../workspace/ui/LoadLegacyDesktopDialog";
import ConversionSummaryDialog from "../../workspace/ui/ConversionSummaryDialog";
import { workspaceTemplateRef } from "./workspaceTemplateRef";
import { useNavbarChartControls } from "../../store/deviceSettingsStore";
import SaveTemplateDialog from "../../workspace/ui/SaveTemplateDialog";
import SaveAsTemplateDialog from "../../workspace/ui/SaveAsTemplateDialog";
import LoadTemplateDialog from "../../workspace/ui/LoadTemplateDialog";
import LoadLegacyTemplateDialog from "../../workspace/ui/LoadLegacyTemplateDialog";
import PasswordDialog from "../../workspace/ui/PasswordDialog";

const WorkspaceMenus = () => {
  const desktopButtonRef = React.useRef(null);
  const templateButtonRef = React.useRef(null);
  const desktopMenuRef = React.useRef(null);
  const templateMenuRef = React.useRef(null);
  const toastRef = React.useRef(null);
  const startupCheckDone = React.useRef(false);
  const [desktopMenuVisible, setDesktopMenuVisible] = React.useState(false);
  const [templateMenuVisible, setTemplateMenuVisible] = React.useState(false);
  // Controles de gráfico fora da navbar → o botão Template do rodapé some
  // (mesmas ações em "Template ▸" no menu de contexto do gráfico).
  const extendedMenuMode = !useNavbarChartControls();

  const clearWorkspace = useChartsStore((state) => state.clearWorkspace);
  const addNewChart = useChartsStore((state) => state.addNewChart);
  const loadWorkspaceAction = useChartsStore((state) => state.loadWorkspace);
  const setDesktopName = useWorkspaceStore((state) => state.setDesktopName);
  const desktopName = useWorkspaceStore((state) => state.desktopName);
  const autoShowLoadDesktopDialog = useSettingsStore(
    (state) => state.autoShowLoadDesktopDialog,
  );
  const settingsInitialized = useSettingsStore((state) => state.isInitialized);

  // Dialog state
  const [saveDialogVisible, setSaveDialogVisible] = React.useState(false);
  const [saveAsDialogVisible, setSaveAsDialogVisible] = React.useState(false);
  const [loadDialogVisible, setLoadDialogVisible] = React.useState(false);
  const [loadLegacyDialogVisible, setLoadLegacyDialogVisible] =
    React.useState(false);
  const [conversionSummaryVisible, setConversionSummaryVisible] =
    React.useState(false);
  const [conversionData, setConversionData] = React.useState(null);
  const [saveTemplateDialogVisible, setSaveTemplateDialogVisible] =
    React.useState(false);
  const [saveAsTemplateDialogVisible, setSaveAsTemplateDialogVisible] =
    React.useState(false);
  const [loadTemplateDialogVisible, setLoadTemplateDialogVisible] =
    React.useState(false);
  const [loadLegacyTemplateDialogVisible, setLoadLegacyTemplateDialogVisible] =
    React.useState(false);

  // Password dialog state
  const [passwordDialogVisible, setPasswordDialogVisible] =
    React.useState(false);
  const [passwordDialogConfig, setPasswordDialogConfig] = React.useState({
    mode: "export",
    title: "",
    message: "",
    onConfirm: null,
    passwordRequired: false,
  });

  // Store file content for password retry
  const pendingImportRef = React.useRef(null);

  // Menu position state for portal rendering
  const [desktopMenuPosition, setDesktopMenuPosition] = React.useState({
    top: 0,
    left: 0,
  });
  const [templateMenuPosition, setTemplateMenuPosition] = React.useState({
    top: 0,
    left: 0,
  });

  // Calculate menu position when opening
  const updateDesktopMenuPosition = React.useCallback(() => {
    if (desktopButtonRef.current) {
      const rect = desktopButtonRef.current.getBoundingClientRect();
      const pos = {
        top: rect.top,
        left: rect.left,
      };
      console.log(
        "[WorkspaceMenus] Desktop button rect:",
        rect,
        "Position:",
        pos,
        "ViewportHeight:",
        window.innerHeight,
      );
      setDesktopMenuPosition(pos);
    }
  }, []);

  const updateTemplateMenuPosition = React.useCallback(() => {
    if (templateButtonRef.current) {
      const rect = templateButtonRef.current.getBoundingClientRect();
      const pos = {
        top: rect.top,
        left: rect.left,
      };
      console.log(
        "[WorkspaceMenus] Template button rect:",
        rect,
        "Position:",
        pos,
        "ViewportHeight:",
        window.innerHeight,
      );
      setTemplateMenuPosition(pos);
    }
  }, []);

  const getTargetTemplateChart = React.useCallback(() => {
    return useChartsStore.getState().getSelectedChart();
  }, []);

  // Handle clicks outside desktop menu
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        desktopMenuRef.current &&
        !desktopMenuRef.current.contains(event.target) &&
        desktopButtonRef.current &&
        !desktopButtonRef.current.contains(event.target)
      ) {
        setDesktopMenuVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle clicks outside template menu
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        templateMenuRef.current &&
        !templateMenuRef.current.contains(event.target) &&
        templateButtonRef.current &&
        !templateButtonRef.current.contains(event.target)
      ) {
        setTemplateMenuVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update menu positions on window resize
  React.useEffect(() => {
    const handleResize = () => {
      if (desktopMenuVisible) updateDesktopMenuPosition();
      if (templateMenuVisible) updateTemplateMenuPosition();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    desktopMenuVisible,
    templateMenuVisible,
    updateDesktopMenuPosition,
    updateTemplateMenuPosition,
  ]);

  // "Meus Desktops" bar "+" slot opens the Load Desktop dialog
  React.useEffect(() => {
    const open = () => setLoadDialogVisible(true);
    eventBus.on(EVENTS.OPEN_LOAD_DESKTOP_DIALOG, open);
    return () => eventBus.off(EVENTS.OPEN_LOAD_DESKTOP_DIALOG, open);
  }, []);

  // Startup desktop check - auto-load or show selection modal
  React.useEffect(() => {
    const checkStartupDesktop = async () => {
      // Only run once
      if (startupCheckDone.current) {
        return;
      }

      // Wait for settings to be initialized (loaded from server after auth)
      if (!settingsInitialized) {
        console.log(
          "[WorkspaceMenus] Waiting for settings to be initialized...",
        );
        return;
      }

      // Only run if no desktop is loaded.
      // NOTE: desktopName is persisted across reloads, so on a fresh startup it
      // rehydrates to the last-loaded name. Skipping here would suppress the
      // startup dialog for every returning user — so honor the toggle: when the
      // user explicitly enabled the startup dialog, proceed regardless.
      if (desktopName !== "Novo desktop" && !autoShowLoadDesktopDialog) {
        console.log(
          "[WorkspaceMenus] Skipping startup check - desktop already loaded:",
          desktopName,
        );
        startupCheckDone.current = true;
        return;
      }

      // Add slight delay to avoid flickering
      await new Promise((resolve) => setTimeout(resolve, 500));

      try {
        console.log(
          "[WorkspaceMenus] Checking for saved desktops on startup...",
        );

        // Step 1: Check for modern saved desktops
        const desktopList = await listDesktopsRemote();

        if (desktopList && desktopList.length > 0) {
          if (desktopList.length === 1) {
            // Exactly 1 desktop - auto-load it
            console.log(
              "[WorkspaceMenus] Found 1 saved desktop, auto-loading:",
              desktopList[0].name,
            );
            await handleLoadDesktopRemote(
              desktopList[0].id,
              desktopList[0].name,
            );
          } else if (autoShowLoadDesktopDialog) {
            // Multiple desktops - show selection dialog (if enabled)
            console.log(
              "[WorkspaceMenus] Found",
              desktopList.length,
              "saved desktops, showing selection dialog",
            );
            setLoadDialogVisible(true);
          } else {
            console.log(
              "[WorkspaceMenus] Found",
              desktopList.length,
              "saved desktops, but auto-show is disabled",
            );
          }
          startupCheckDone.current = true;
          return;
        }

        // Step 2: No modern desktops, check for legacy desktops
        console.log(
          "[WorkspaceMenus] No saved desktops found, checking for legacy desktops...",
        );

        // Import services dynamically
        const { default: storageService } = await import(
          "~/data/Services/storageService"
        );
        const { default: marketdata } = await import(
          "~/data/Services/marketdata"
        );

        // Get valid token for legacy API
        const token = await marketdata.getValidToken();
        if (!token) {
          console.warn(
            "[WorkspaceMenus] No auth token available for legacy desktop check",
          );
          startupCheckDone.current = true;
          return;
        }

        // Initialize storageService
        const url = marketdata.getUrl();
        storageService.init(url, token);

        // Get legacy desktop list
        const legacyList = await storageService.getLegacyDesktopList();

        if (legacyList && legacyList.length > 0) {
          if (legacyList.length === 1) {
            // Exactly 1 legacy desktop - auto-import it
            console.log(
              "[WorkspaceMenus] Found 1 legacy desktop, auto-importing:",
              legacyList[0].name,
            );
            await handleImportLegacyDesktop(
              legacyList[0].chartid,
              legacyList[0].name,
            );
          } else {
            // Multiple legacy desktops - show selection dialog
            console.log(
              "[WorkspaceMenus] Found",
              legacyList.length,
              "legacy desktops, showing selection dialog",
            );
            setLoadLegacyDialogVisible(true);
          }
        } else {
          console.log("[WorkspaceMenus] No legacy desktops found");
        }
      } catch (error) {
        console.error(
          "[WorkspaceMenus] Error during startup desktop check:",
          error,
        );
        // Silently fail - don't block the app or show error toast
      } finally {
        startupCheckDone.current = true;
      }
    };

    checkStartupDesktop();
  }, [desktopName, autoShowLoadDesktopDialog, settingsInitialized]); // Re-run when settings are initialized or change

  // Desktop menu handlers
  const handleDesktopMenuToggle = () => {
    if (!desktopMenuVisible) {
      // Calculate position synchronously before showing
      if (desktopButtonRef.current) {
        const rect = desktopButtonRef.current.getBoundingClientRect();
        console.log(
          "[WorkspaceMenus] Opening desktop menu. Button rect:",
          rect,
        );
        setDesktopMenuPosition({
          top: rect.top,
          left: rect.left,
        });
      }
    }
    setDesktopMenuVisible(!desktopMenuVisible);
  };

  const handleNewWorkspace = () => {
    clearWorkspace();
    // Reset dock layout so panels (Positions, Open Orders, Log) are closed (#200)
    useWorkspaceStore.getState().setDockLayout(null);
    useWorkspaceStore.getState().setRouterOperationsSidebarOpen(false);
    useWorkspaceStore.getState().incrementDockLayoutKey();
    useWatchlistStore.getState().resetWatchlists();
    useBookByPriceStore.getState().reset();
    addNewChart();
    setDesktopName("Novo desktop");
    useWorkspaceStore.getState().setCurrentDesktopId(null);
    setDesktopMenuVisible(false);
    toastRef.current?.show({
      severity: "success",
      summary: "Novo Desktop",
      detail: "Novo desktop criado com sucesso",
      life: 3000,
    });
  };

  const handleImportWorkspace = async (password = "") => {
    try {
      setDesktopMenuVisible(false);

      // If we have pending content, use it (password retry)
      if (pendingImportRef.current && password) {
        const { content, isDesktop } = pendingImportRef.current;
        await processImportContent(content, password, isDesktop);
        pendingImportRef.current = null;
        return;
      }

      // Normal flow: open file picker
      // clearWorkspace is called via wrappedLoadAction before applying data
      const wrappedLoadAction = (...args) => {
        clearWorkspace();
        return loadWorkspaceAction(...args);
      };
      const result = await importWorkspace(wrappedLoadAction, password);

      if (result.success) {
        setDesktopName("Novo desktop");
        // Carregado de disco = NÃO-favorito: id sintético → slot fantasma +
        // protege o cache local do favorito anterior (I4). Ver Silverlight import.
        useWorkspaceStore.getState().setCurrentDesktopId(uuidv4());
        toastRef.current?.show({
          severity: "success",
          summary: "Desktop Importado",
          detail: result.encrypted
            ? `Desktop importado com sucesso (${result.passwordRequired ? "protegido por senha" : "obfuscado"})`
            : "Desktop importado com sucesso",
          life: 3000,
        });
        pendingImportRef.current = null;
      } else if (!result.cancelled) {
        throw new Error("Falha ao importar desktop");
      }
    } catch (error) {
      console.error("Failed to import workspace:", error);

      // Check if password is required
      if (error.message === "PASSWORD_REQUIRED" && error.fileContent) {
        // Store the file content for retry
        pendingImportRef.current = {
          content: error.fileContent,
          isDesktop: true,
        };

        setPasswordDialogConfig({
          mode: "import",
          title: "Senha Necessária",
          message:
            "Este arquivo está protegido por senha. Digite a senha para importar:",
          passwordRequired: true,
          onConfirm: (password) => {
            setPasswordDialogVisible(false);
            handleImportWorkspace(password);
          },
        });
        setPasswordDialogVisible(true);
        return;
      }

      pendingImportRef.current = null;
      toastRef.current?.show({
        severity: "error",
        summary: "Erro ao Importar",
        detail: error.message || "Falha ao importar o desktop",
        life: 5000,
      });
    }
  };

  // Process import content (shared by desktop and template)
  const processImportContent = async (content, password, isDesktop) => {
    const { decryptDesktop, checkEncryption, isPlainJson } = await import(
      "../../workspace/utils/encryption.js"
    );
    const { deserializeWorkspace } = await import(
      "../../workspace/core/serialization.js"
    );
    const { deserializeTemplate } = await import(
      "../../workspace/core/templateSerialization.js"
    );

    // Check if encrypted
    const encryptionInfo = checkEncryption(content);
    const isEncryptedFile = encryptionInfo !== null;

    let data;

    if (isEncryptedFile) {
      // Decrypt
      const decryptedJson = await decryptDesktop(content, password);
      data = JSON.parse(decryptedJson);
    } else if (isPlainJson(content)) {
      // Legacy plain JSON format
      data = JSON.parse(content);
    } else {
      throw new Error("Invalid file format");
    }

    // Apply the data
    if (isDesktop) {
      clearWorkspace();
      const success = deserializeWorkspace(data, loadWorkspaceAction);
      if (success) {
        setDesktopName("Novo desktop");
        toastRef.current?.show({
          severity: "success",
          summary: "Desktop Importado",
          detail: isEncryptedFile
            ? `Desktop importado com sucesso (${encryptionInfo?.passwordRequired ? "protegido por senha" : "obfuscado"})`
            : "Desktop importado com sucesso",
          life: 3000,
        });
      }
    } else {
      // Template import
      const store = useChartsStore.getState();
      const selectedChart = store.charts[store.selectedTab];
      const success = await deserializeTemplate(data, store, selectedChart.id);
      if (success) {
        toastRef.current?.show({
          severity: "success",
          summary: "Template Importado",
          detail: isEncryptedFile
            ? `Template importado com sucesso (${encryptionInfo?.passwordRequired ? "protegido por senha" : "obfuscado"})`
            : "Template importado com sucesso",
          life: 3000,
        });
      }
    }
  };

  const handleImportLegacyDesktop = async (desktopId, desktopName) => {
    try {
      console.log(
        "[WorkspaceMenus] Importing legacy desktop:",
        desktopId,
        desktopName,
      );

      // Import storageService dynamically to avoid circular dependencies
      const { default: storageService } = await import(
        "~/data/Services/storageService"
      );

      // Get legacy desktop from LegacyStorage API
      const legacyDesktop = await storageService.getLegacyDesktop(desktopId);

      if (!legacyDesktop || !legacyDesktop.content) {
        throw new Error(
          "Desktop do WebCharts Antigo não encontrado ou inválido",
        );
      }

      console.log("[WorkspaceMenus] Legacy desktop fetched:", {
        name: legacyDesktop.name,
        chartid: legacyDesktop.chartid,
        timestamp: legacyDesktop.timestamp,
      });

      // Convert legacy workspace to Odin format using Phase 1 converter
      console.log("[WorkspaceMenus] Converting legacy workspace...");
      const convertedWorkspace = convertLegacyWorkspace(legacyDesktop);

      // Validate converted workspace
      const isValid = validateConvertedWorkspace(convertedWorkspace);
      if (!isValid) {
        throw new Error("Workspace convertido é inválido");
      }

      // Get conversion stats
      const stats = getConversionStats(legacyDesktop, convertedWorkspace);
      console.log("[WorkspaceMenus] Conversion stats:", stats);

      // Load converted workspace into the app
      console.log("[WorkspaceMenus] Loading converted workspace...");
      clearWorkspace();
      const success = deserializeWorkspace(
        convertedWorkspace,
        loadWorkspaceAction,
      );

      if (!success) {
        throw new Error("Falha ao carregar workspace convertido");
      }

      // Update desktop name with imported legacy name
      const importedName = `${desktopName} (Importado)`;
      setDesktopName(importedName);
      // Carregado NÃO-favorito: id sintético → slot fantasma + protege o cache
      // local do favorito anterior (I4). Ver handleImportSilverlightDesktop.
      useWorkspaceStore.getState().setCurrentDesktopId(uuidv4());
      console.log("[WorkspaceMenus] Desktop name set to:", importedName);

      // Close the legacy import dialog
      setLoadLegacyDialogVisible(false);

      // Show conversion summary modal
      setConversionData({
        desktopName,
        workspace: convertedWorkspace,
        stats,
      });
      setConversionSummaryVisible(true);

      console.log("[WorkspaceMenus] ✅ Legacy desktop imported successfully");
    } catch (error) {
      console.error("[WorkspaceMenus] Failed to import legacy desktop:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Erro ao Importar",
        detail: error.message || "Falha ao importar do WebCharts antigo",
        life: 5000,
      });
      throw error;
    }
  };

  // Import de desktop do app Silverlight (tab Silverlight do mesmo modal).
  // Mesmo caminho de aplicação do import legado; a diferença é a origem
  // (payload base64 → sldZip → conversor puro) e o organizar pós-load:
  // grid/abas NUNCA vai no workspace object — vai via setRequestOrganize.
  const handleImportSilverlightDesktop = async (silverlightName) => {
    try {
      const { default: storageService } = await import(
        "~/data/Services/storageService"
      );
      // identificador no storage Silverlight é o próprio name (PK login+name)
      const item = await storageService.getSilverlightItem(
        "desktop",
        silverlightName,
      );
      if (!item || !item.payload) {
        throw new Error(
          "Desktop do Silverlight não encontrado ou sem conteúdo",
        );
      }

      const { decodeSilverlightDesktop } = await import(
        "~/workspace/storage/sldZip"
      );
      const { convertSilverlightDesktop } = await import(
        "~/workspace/storage/silverlightConverter"
      );

      const desk = await decodeSilverlightDesktop(item.payload);
      const { workspace, stats, layoutMode } = convertSilverlightDesktop(desk);

      if (!validateConvertedWorkspace(workspace)) {
        throw new Error("Workspace convertido é inválido");
      }

      clearWorkspace();
      // #438: dock do zero. Sem isso o desktop importado herdava watchlist,
      // livro e DoM do desktop que estava aberto — clearWorkspace() só conhece
      // charts, e o workspace convertido não traz layout (o grid/abas vem do
      // setRequestOrganize logo abaixo).
      const success = deserializeWorkspace(workspace, loadWorkspaceAction, {
        resetDock: true,
      });
      if (!success) {
        throw new Error("Falha ao carregar workspace convertido");
      }

      const displayName = silverlightName || "default";
      setDesktopName(`${displayName} (Importado)`);
      // Desktop importado = carregado NÃO-favorito: id sintético (não está em
      // favoritos nem na lista do servidor) joga a pill no slot fantasma e evita
      // que o autosave sobrescreva o cache local do desktop favorito anterior (I4).
      useWorkspaceStore.getState().setCurrentDesktopId(uuidv4());
      setLoadLegacyDialogVisible(false);

      // Injeta o painel Info (colado à direita) ao montar o layout deste desktop
      // importado, se a feature estiver ligada. Consumido uma vez pelo efeito
      // requestOrganize do DockModeLayout. Desktops Silverlight não trazem Info.
      if (isFeatureEnabled("infoPanel")) {
        useWorkspaceStore.getState().setPendingInfoInject(true);
      }

      // mosaico → grid; abas → tabs (regra do censo, decidida no conversor)
      useWorkspaceStore.getState().setRequestOrganize(layoutMode);

      setConversionData({
        desktopName: displayName,
        workspace,
        stats,
        sourceLabel: "Silverlight",
      });
      setConversionSummaryVisible(true);
    } catch (error) {
      console.error(
        "[WorkspaceMenus] Failed to import silverlight desktop:",
        error,
      );
      toastRef.current?.show({
        severity: "error",
        summary: "Erro ao Importar",
        detail: error.message || "Falha ao importar do Silverlight",
        life: 5000,
      });
      throw error;
    }
  };

  // Import de template do app Silverlight (tab Silverlight do modal de
  // templates). Contrato do template odin = SÓ indicadores + tipo de gráfico
  // (mesmo do import legado); desenhos/escala/símbolo do ChartTpl não entram —
  // o que ficou de fora vira aviso no resumo/toast.
  const handleImportSilverlightTemplate = async (silverlightName) => {
    try {
      const { default: storageService } = await import(
        "~/data/Services/storageService"
      );
      // identificador no storage Silverlight é o próprio name (PK login+name)
      const item = await storageService.getSilverlightItem(
        "template",
        silverlightName,
      );
      if (!item || !item.payload) {
        throw new Error(
          "Template do Silverlight não encontrado ou sem conteúdo",
        );
      }

      // payload de template é JSON UTF-8 puro (sem zip/base64, ≠ desktop)
      const { convertSilverlightChartTpl } = await import(
        "~/workspace/storage/silverlightConverter"
      );
      const r = convertSilverlightChartTpl(item.payload);

      const selectedChart = getTargetTemplateChart();
      if (!selectedChart) {
        throw new Error("Nenhum gráfico selecionado");
      }

      const odinTemplate = {
        version: "1.0",
        timestamp: new Date().toISOString(),
        chartType: r.chart.chartType,
        indicators: r.chart.indicators,
        metadata: {
          app: "Odin Sight (from Silverlight)",
          description: "Template convertido do Silverlight",
          sourceSymbol: r.chart.symbol,
          sourcePeriodicity: r.chart.periodicity,
        },
      };

      const success = deserializeTemplate(
        odinTemplate,
        useChartsStore.getState(),
        selectedChart.id,
      );
      if (!success) {
        throw new Error("Falha ao aplicar template ao gráfico");
      }

      setLoadLegacyTemplateDialogVisible(false);

      if (r.skippedIndicators.length > 0) {
        // houve conteúdo sem par — mostra o resumo (lista skips com motivo)
        setConversionData({
          desktopName: silverlightName,
          itemLabel: "Template",
          workspace: { charts: [r.chart] },
          stats: {
            charts: 1,
            indicators: r.chart.indicators.length,
            drawings: 0,
            skippedIndicators: r.skippedIndicators,
            failedIndicators: [],
          },
          sourceLabel: "Silverlight",
        });
        setConversionSummaryVisible(true);
      } else {
        const extra =
          r.drawingsConverted > 0
            ? " (desenhos do template não são aplicados)"
            : "";
        toastRef.current?.show({
          severity: "success",
          summary: "Template Importado",
          detail: `Template "${silverlightName}" do Silverlight aplicado com sucesso${extra}`,
          life: 3000,
        });
      }
    } catch (error) {
      console.error(
        "[WorkspaceMenus] Failed to import silverlight template:",
        error,
      );
      toastRef.current?.show({
        severity: "error",
        summary: "Erro ao Importar",
        detail: error.message || "Falha ao importar do Silverlight",
        life: 5000,
      });
      throw error;
    }
  };

  const handleExportWorkspace = () => {
    setDesktopMenuVisible(false);
    setPasswordDialogConfig({
      mode: "export",
      title: "Proteger Desktop",
      message: "Defina uma senha para proteger o arquivo exportado:",
      passwordRequired: false,
      onConfirm: async (password) => {
        setPasswordDialogVisible(false);
        try {
          const result = await exportWorkspace(
            useChartsStore.getState(),
            null,
            password,
          );
          toastRef.current?.show({
            severity: "success",
            summary: "Desktop Exportado",
            detail: result.passwordProtected
              ? "Desktop exportado com proteção por senha"
              : "Desktop exportado com proteção básica",
            life: 3000,
          });
        } catch (error) {
          console.error("Failed to export workspace:", error);
          toastRef.current?.show({
            severity: "error",
            summary: "Erro ao Exportar",
            detail: error.message || "Falha ao exportar o desktop",
            life: 5000,
          });
        }
      },
    });
    setPasswordDialogVisible(true);
  };

  const handleQuickSaveDesktop = async () => {
    try {
      setDesktopMenuVisible(false);
      const existingDesktop = await desktopNameExists(desktopName);
      let saved;
      if (existingDesktop) {
        // Overwrite existing
        saved = await saveDesktopRemote(
          useChartsStore.getState(),
          desktopName,
          {
            id: existingDesktop.id,
          },
        );
      } else {
        saved = await saveDesktopRemote(useChartsStore.getState(), desktopName);
      }
      markDesktopSavedToServer(useChartsStore.getState(), saved?.id);
      eventBus.emit(EVENTS.DESKTOP_LIST_CHANGED);
      toastRef.current?.show({
        severity: "success",
        summary: "Desktop Salvo",
        detail: `Desktop "${desktopName}" salvo com sucesso`,
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to save desktop:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Erro ao Salvar",
        detail: "Falha ao salvar o desktop",
        life: 5000,
      });
    }
  };

  const handleSaveDesktopRemote = async (name) => {
    try {
      const existingDesktop = await desktopNameExists(name);
      let saved;
      if (existingDesktop) {
        // Overwrite existing
        saved = await saveDesktopRemote(useChartsStore.getState(), name, {
          id: existingDesktop.id,
        });
      } else {
        saved = await saveDesktopRemote(useChartsStore.getState(), name);
      }
      markDesktopSavedToServer(useChartsStore.getState(), saved?.id);
      eventBus.emit(EVENTS.DESKTOP_LIST_CHANGED);
      setDesktopName(name);
      setSaveDialogVisible(false);
      toastRef.current?.show({
        severity: "success",
        summary: "Desktop Salvo",
        detail: `Desktop "${name}" salvo com sucesso`,
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to save desktop:", error);
      throw error;
    }
  };

  const handleSaveAsDesktop = async (name, existingId = null) => {
    try {
      let saved;
      if (existingId) {
        // Overwrite existing desktop
        saved = await saveDesktopRemote(useChartsStore.getState(), name, {
          id: existingId,
        });
      } else {
        // Save as new desktop
        saved = await saveDesktopRemote(useChartsStore.getState(), name);
      }
      markDesktopSavedToServer(useChartsStore.getState(), saved?.id);
      eventBus.emit(EVENTS.DESKTOP_LIST_CHANGED);
      setDesktopName(name);
      setSaveAsDialogVisible(false);
      toastRef.current?.show({
        severity: "success",
        summary: "Desktop Salvo",
        detail: `Desktop "${name}" salvo com sucesso`,
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to save desktop:", error);
      throw error;
    }
  };

  const handleLoadDesktopRemote = async (id, desktopName) => {
    try {
      const wrappedLoadAction = (...args) => {
        clearWorkspace();
        return loadWorkspaceAction(...args);
      };
      // "Meus Desktops": persists the current desktop locally before loading,
      // and a dirty local copy of the target wins over the server
      await switchToDesktop(id, desktopName, {
        getChartsState: () => useChartsStore.getState(),
        loadCharts: wrappedLoadAction,
      });
      toastRef.current?.show({
        severity: "success",
        summary: "Desktop Carregado",
        detail: "Desktop carregado com sucesso",
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to load desktop:", error);
      throw error;
    }
  };

  // Delete must also drop the local copy, favorites and active-desktop tracking
  const handleDeleteDesktopRemote = async (id) => {
    await deleteDesktopRemote(id);
    const { user, broker } = getWorkspaceContext();
    clearDesktopCache(user, broker, id);
    useSettingsStore.getState().removeFavoriteDesktop(id);
    const workspaceStore = useWorkspaceStore.getState();
    if (workspaceStore.currentDesktopId === id) {
      workspaceStore.setCurrentDesktopId(null);
    }
    eventBus.emit(EVENTS.DESKTOP_LIST_CHANGED);
  };

  // "Recarregar do servidor" (caller already confirmed the discard)
  const handleReloadDesktopFromServer = async (id, name) => {
    const wrappedLoadAction = (...args) => {
      clearWorkspace();
      return loadWorkspaceAction(...args);
    };
    await reloadDesktopFromServer(id, name, {
      getChartsState: () => useChartsStore.getState(),
      loadCharts: wrappedLoadAction,
    });
  };

  const handleRenameDesktopRemote = async (id, newName) => {
    const result = await renameDesktopRemote(id, newName);
    eventBus.emit(EVENTS.DESKTOP_LIST_CHANGED);
    return result;
  };

  const handleDuplicateDesktopRemote = async (id, newName) => {
    const result = await duplicateDesktopRemote(id, newName);
    eventBus.emit(EVENTS.DESKTOP_LIST_CHANGED);
    return result;
  };

  // Template menu handlers
  const handleTemplateMenuToggle = () => {
    if (!templateMenuVisible) {
      // Calculate position synchronously before showing
      if (templateButtonRef.current) {
        const rect = templateButtonRef.current.getBoundingClientRect();
        console.log(
          "[WorkspaceMenus] Opening template menu. Button rect:",
          rect,
        );
        setTemplateMenuPosition({
          top: rect.top,
          left: rect.left,
        });
      }
    }
    setTemplateMenuVisible(!templateMenuVisible);
  };

  const handleSaveTemplateRemote = async (name) => {
    try {
      const selectedChart = getTargetTemplateChart();
      if (!selectedChart) {
        throw new Error("Nenhum gráfico selecionado");
      }
      await saveTemplateRemote(selectedChart, name);
      setSaveTemplateDialogVisible(false);
      toastRef.current?.show({
        severity: "success",
        summary: "Template Salvo",
        detail: `Template "${name}" salvo com sucesso`,
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to save template:", error);
      throw error;
    }
  };

  const handleSaveAsTemplate = async (name, existingId = null) => {
    try {
      const selectedChart = getTargetTemplateChart();
      if (!selectedChart) {
        throw new Error("Nenhum gráfico selecionado");
      }

      if (existingId) {
        // Overwrite existing template - need to delete first, then save with new content
        await deleteTemplateRemote(existingId);
      }

      // Save template (new or overwrite after delete)
      await saveTemplateRemote(selectedChart, name);
      setSaveAsTemplateDialogVisible(false);
      toastRef.current?.show({
        severity: "success",
        summary: "Template Salvo",
        detail: `Template "${name}" salvo com sucesso`,
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to save template:", error);
      throw error;
    }
  };

  const handleLoadTemplateRemote = async (id) => {
    try {
      const selectedChart = getTargetTemplateChart();
      if (!selectedChart) {
        throw new Error("Nenhum gráfico selecionado");
      }
      await loadTemplateRemote(id, useChartsStore.getState(), selectedChart.id);
      toastRef.current?.show({
        severity: "success",
        summary: "Template Aplicado",
        detail: "Template aplicado ao gráfico com sucesso",
        life: 3000,
      });
    } catch (error) {
      console.error("Failed to load template:", error);
      throw error;
    }
  };

  const handleExportTemplate = () => {
    setTemplateMenuVisible(false);
    const selectedChart = getTargetTemplateChart();
    if (!selectedChart) {
      toastRef.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Nenhum gráfico selecionado",
        life: 3000,
      });
      return;
    }

    setPasswordDialogConfig({
      mode: "export",
      title: "Proteger Template",
      message: "Defina uma senha para proteger o template exportado:",
      passwordRequired: false,
      onConfirm: async (password) => {
        setPasswordDialogVisible(false);
        try {
          const result = await exportTemplate(selectedChart, null, password);
          toastRef.current?.show({
            severity: "success",
            summary: "Template Exportado",
            detail: result.passwordProtected
              ? "Template exportado com proteção por senha"
              : "Template exportado com proteção básica",
            life: 3000,
          });
        } catch (error) {
          console.error("Failed to export template:", error);
          toastRef.current?.show({
            severity: "error",
            summary: "Erro ao Exportar",
            detail: error.message || "Falha ao exportar template",
            life: 5000,
          });
        }
      },
    });
    setPasswordDialogVisible(true);
  };

  const handleImportTemplate = async (password = "") => {
    try {
      setTemplateMenuVisible(false);
      const selectedChart = getTargetTemplateChart();
      if (!selectedChart) {
        throw new Error("Nenhum gráfico selecionado");
      }

      // If we have pending content, use it (password retry)
      if (pendingImportRef.current && password) {
        const { content, isDesktop } = pendingImportRef.current;
        await processImportContent(content, password, isDesktop);
        pendingImportRef.current = null;
        return;
      }

      const applyTemplate = async (templateData, chartId) => {
        return deserializeTemplate(
          templateData,
          useChartsStore.getState(),
          chartId,
        );
      };
      const result = await importTemplate(
        applyTemplate,
        selectedChart.id,
        password,
      );

      if (result.success) {
        toastRef.current?.show({
          severity: "success",
          summary: "Template Importado",
          detail: result.encrypted
            ? `Template importado com sucesso (${result.passwordRequired ? "protegido por senha" : "obfuscado"})`
            : "Template importado com sucesso",
          life: 3000,
        });
        pendingImportRef.current = null;
      } else if (!result.cancelled) {
        throw new Error("Falha ao importar template");
      }
    } catch (error) {
      console.error("Failed to import template:", error);

      // Check if password is required
      if (error.message === "PASSWORD_REQUIRED" && error.fileContent) {
        // Store the file content for retry
        pendingImportRef.current = {
          content: error.fileContent,
          isDesktop: false, // This is a template
        };

        setPasswordDialogConfig({
          mode: "import",
          title: "Senha Necessária",
          message:
            "Este template está protegido por senha. Digite a senha para importar:",
          passwordRequired: true,
          onConfirm: (password) => {
            setPasswordDialogVisible(false);
            handleImportTemplate(password);
          },
        });
        setPasswordDialogVisible(true);
        return;
      }

      pendingImportRef.current = null;
      toastRef.current?.show({
        severity: "error",
        summary: "Erro ao Importar",
        detail: error.message || "Falha ao importar template",
        life: 5000,
      });
    }
  };

  const handleImportLegacyTemplate = async (templateId, templateName) => {
    try {
      console.log(
        "[WorkspaceMenus] Importing legacy template:",
        templateId,
        templateName,
      );

      // Import storageService dynamically to avoid circular dependencies
      const { default: storageService } = await import(
        "~/data/Services/storageService"
      );

      // Get the template list (will auto-detect and unwrap if needed)
      const templateList = await storageService.getLegacyTemplateList();

      // Find the specific template by name
      const template = templateList.find((t) => t.name === templateName);

      if (!template) {
        throw new Error(`Template "${templateName}" não encontrado`);
      }

      console.log("[WorkspaceMenus] Found template:", {
        name: template.name,
        isUnwrapped: template._isUnwrapped || false,
      });

      // Get template data
      let legacyTemplateData;

      if (template._isUnwrapped && template._templateData) {
        // Unwrapped by client - use the data directly
        console.log("[WorkspaceMenus] Using unwrapped template data");
        legacyTemplateData = template._templateData;
      } else {
        // Not unwrapped - fetch content
        console.log("[WorkspaceMenus] Fetching template content");
        const fullTemplate = await storageService.getLegacyTemplate(
          template.chartid,
        );

        if (!fullTemplate || !fullTemplate.content) {
          throw new Error("Template não encontrado ou inválido");
        }

        legacyTemplateData = JSON.parse(fullTemplate.content);
      }

      console.log(
        "[WorkspaceMenus] Legacy template JSON (ChartIQ format):",
        JSON.stringify(legacyTemplateData, null, 2),
      );

      // Convert ChartIQ format → Odin Sight format
      const { convertIndicators } = await import(
        "~/workspace/storage/legacyConverter"
      );
      const { indicators } = convertIndicators(
        legacyTemplateData.studies || {},
      );

      // Create Odin-format template
      const odinTemplate = {
        version: "1.0",
        timestamp: new Date().toISOString(),
        chartType: legacyTemplateData.chartType || "candle",
        indicators: indicators,
        metadata: {
          app: "Odin Sight (from legacy WebCharts)",
          description: "Converted template from legacy ChartIQ format",
          originalInterval: legacyTemplateData.interval,
          originalPeriodicity: legacyTemplateData.periodicity,
        },
      };

      console.log(
        "[WorkspaceMenus] Converted to Odin format:",
        JSON.stringify(odinTemplate, null, 2),
      );

      // Get selected chart
      const selectedChart = getTargetTemplateChart();
      if (!selectedChart) {
        throw new Error("Nenhum gráfico selecionado");
      }

      // Apply template to selected chart
      console.log("[WorkspaceMenus] Applying template to chart...");
      const success = deserializeTemplate(
        odinTemplate,
        useChartsStore.getState(),
        selectedChart.id,
      );

      if (!success) {
        throw new Error("Falha ao aplicar template ao gráfico");
      }

      // Close the legacy import dialog
      setLoadLegacyTemplateDialogVisible(false);

      toastRef.current?.show({
        severity: "success",
        summary: "Template Importado",
        detail: `Template "${templateName}" do WebCharts Antigo aplicado com sucesso`,
        life: 3000,
      });

      console.log("[WorkspaceMenus] ✅ Legacy template imported successfully");
    } catch (error) {
      console.error(
        "[WorkspaceMenus] Failed to import legacy template:",
        error,
      );
      toastRef.current?.show({
        severity: "error",
        summary: "Erro ao Importar",
        detail: error.message || "Falha ao importar do WebCharts antigo",
        life: 5000,
      });
      throw error;
    }
  };

  const isNewDesktop = desktopName === "Novo desktop";

  const desktopMenuItems = [
    { label: "Novo", icon: "pi pi-plus", command: handleNewWorkspace },
    { separator: true },
    {
      label: isNewDesktop ? "Salvar..." : `Salvar ${desktopName}`,
      icon: "pi pi-save",
      command: isNewDesktop
        ? () => {
            setDesktopMenuVisible(false);
            setSaveDialogVisible(true);
          }
        : handleQuickSaveDesktop,
    },
    {
      label: "Salvar como...",
      icon: "pi pi-copy",
      command: () => {
        setDesktopMenuVisible(false);
        setSaveAsDialogVisible(true);
      },
    },
    {
      label: "Carregar...",
      icon: "pi pi-folder-open",
      command: () => {
        setDesktopMenuVisible(false);
        setLoadDialogVisible(true);
      },
    },
    { separator: true },
    {
      label: "Exportar para arquivo...",
      icon: "pi pi-download",
      command: handleExportWorkspace,
    },
    {
      label: "Importar de arquivo...",
      icon: "pi pi-upload",
      command: handleImportWorkspace,
    },
    { separator: true },
    {
      label: "Importar do WebCharts Antigo...",
      icon: "pi pi-file-import",
      command: () => {
        setDesktopMenuVisible(false);
        setLoadLegacyDialogVisible(true);
      },
    },
  ];

  // Expose the Template actions to the chart context menu ("Template ▸") —
  // the dialogs live HERE, the menu only triggers them. Re-attached every
  // render so the handlers are never stale; detached on unmount.
  React.useEffect(() => {
    workspaceTemplateRef.current = {
      saveAs: () => setSaveAsTemplateDialogVisible(true),
      load: () => setLoadTemplateDialogVisible(true),
      exportFile: handleExportTemplate,
      importFile: handleImportTemplate,
      importLegacy: () => setLoadLegacyTemplateDialogVisible(true),
    };
  });
  React.useEffect(
    () => () => {
      workspaceTemplateRef.current = null;
    },
    [],
  );

  const templateMenuItems = [
    {
      label: "Salvar como...",
      icon: "pi pi-save",
      command: () => {
        setTemplateMenuVisible(false);
        setSaveAsTemplateDialogVisible(true);
      },
    },
    {
      label: "Carregar...",
      icon: "pi pi-folder-open",
      command: () => {
        setTemplateMenuVisible(false);
        setLoadTemplateDialogVisible(true);
      },
    },
    { separator: true },
    {
      label: "Exportar para arquivo...",
      icon: "pi pi-download",
      command: handleExportTemplate,
    },
    {
      label: "Importar de arquivo...",
      icon: "pi pi-upload",
      command: handleImportTemplate,
    },
    { separator: true },
    {
      label: "Importar do WebCharts Antigo...",
      icon: "pi pi-file-import",
      command: () => {
        setTemplateMenuVisible(false);
        setLoadLegacyTemplateDialogVisible(true);
      },
    },
  ];

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      {/* Desktop Menu */}
      <div style={{ position: "relative" }}>
        <Button
          ref={desktopButtonRef}
          icon="pi pi-desktop"
          label={`Desktop: ${desktopName}`}
          onClick={handleDesktopMenuToggle}
          className="p-button-text p-button-sm footer-workspace-btn workspace-button-bg"
          style={{
            fontSize: "12px",
            padding: "2px 10px",
            borderRadius: 6,
            boxShadow: "0 1px 4px 0 rgba(0,0,0,0.04)",
            border: "none",
            color: "var(--text-color)",
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
          }}
          data-evo-id="workspacemenus-btn-desktop"
        />
        {desktopMenuVisible &&
          ReactDOM.createPortal(
            <div
              ref={desktopMenuRef}
              className="p-menu p-component p-menu-overlay footer-portal-menu"
              style={{
                position: "fixed",
                top: "auto",
                bottom: `calc(100vh - ${desktopMenuPosition.top}px + 5px)`,
                left: desktopMenuPosition.left,
                minWidth: 250,
                maxHeight: 400,
                overflowY: "auto",
                zIndex: 10000,
                backgroundColor: "#ffffff",
                borderRadius: "4px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
                border: "1px solid #dee2e6",
              }}
            >
              <ul
                className="p-menu-list"
                style={{ margin: 0, padding: "0.25rem 0", listStyle: "none" }}
              >
                {desktopMenuItems.map((item, index) =>
                  item.separator ? (
                    <li key={index} className="p-menu-separator" />
                  ) : (
                    <li key={index} className="p-menuitem">
                      <a
                        className="p-menuitem-link"
                        onClick={(e) => {
                          e.preventDefault();
                          item.command();
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          padding: "0.75rem 1rem",
                          cursor: "pointer",
                        }}
                        data-evo-id="workspacemenus-link-desktop-item"
                      >
                        {item.icon && (
                          <span className={`p-menuitem-icon ${item.icon}`} />
                        )}
                        <span className="p-menuitem-text">{item.label}</span>
                      </a>
                    </li>
                  ),
                )}
              </ul>
            </div>,
            document.body,
          )}
      </div>

      {/* Template button hidden while the extended chart menu is ON — the
          same actions live under "Template ▸" in the chart context menu. The
          dialogs below stay mounted either way (the menu triggers them via
          workspaceTemplateRef). */}
      {!extendedMenuMode && (
        <Divider
          layout="vertical"
          style={{ height: "24px", margin: "0 4px" }}
        />
      )}

      {/* Template Menu */}
      <div
        style={
          extendedMenuMode ? { display: "none" } : { position: "relative" }
        }
      >
        <Button
          ref={templateButtonRef}
          icon="pi pi-palette"
          label="Template"
          onClick={handleTemplateMenuToggle}
          className="p-button-text p-button-sm footer-workspace-btn workspace-button-bg"
          style={{
            fontSize: "12px",
            padding: "2px 10px",
            borderRadius: 6,
            boxShadow: "0 1px 4px 0 rgba(0,0,0,0.04)",
            border: "none",
            color: "var(--text-color)",
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
          }}
          data-evo-id="workspacemenus-btn-template"
        />
        {templateMenuVisible &&
          ReactDOM.createPortal(
            <div
              ref={templateMenuRef}
              className="p-menu p-component p-menu-overlay footer-portal-menu"
              style={{
                position: "fixed",
                top: "auto",
                bottom: `calc(100vh - ${templateMenuPosition.top}px + 5px)`,
                left: templateMenuPosition.left,
                minWidth: 250,
                maxHeight: 400,
                overflowY: "auto",
                zIndex: 10000,
                backgroundColor: "#ffffff",
                borderRadius: "4px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
                border: "1px solid #dee2e6",
              }}
            >
              <ul
                className="p-menu-list"
                style={{ margin: 0, padding: "0.25rem 0", listStyle: "none" }}
              >
                {templateMenuItems.map((item, index) =>
                  item.separator ? (
                    <li key={index} className="p-menu-separator" />
                  ) : (
                    <li key={index} className="p-menuitem">
                      <a
                        className="p-menuitem-link"
                        onClick={(e) => {
                          e.preventDefault();
                          item.command();
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          padding: "0.75rem 1rem",
                          cursor: "pointer",
                        }}
                        data-evo-id="workspacemenus-link-template-item"
                      >
                        {item.icon && (
                          <span className={`p-menuitem-icon ${item.icon}`} />
                        )}
                        <span className="p-menuitem-text">{item.label}</span>
                      </a>
                    </li>
                  ),
                )}
              </ul>
            </div>,
            document.body,
          )}
      </div>

      {/* Toast */}
      <Toast ref={toastRef} position="top-center" />

      {/* Dialogs */}
      <SaveDesktopDialog
        visible={saveDialogVisible}
        onHide={() => setSaveDialogVisible(false)}
        onSave={handleSaveDesktopRemote}
      />
      <SaveAsDesktopDialog
        visible={saveAsDialogVisible}
        onHide={() => setSaveAsDialogVisible(false)}
        onSave={handleSaveAsDesktop}
        onRefresh={listDesktopsRemote}
      />
      <LoadDesktopDialog
        visible={loadDialogVisible}
        onHide={() => setLoadDialogVisible(false)}
        onLoad={handleLoadDesktopRemote}
        onDuplicate={handleDuplicateDesktopRemote}
        onRename={handleRenameDesktopRemote}
        onDelete={handleDeleteDesktopRemote}
        onRefresh={listDesktopsRemote}
        onReloadFromServer={handleReloadDesktopFromServer}
      />
      <LoadLegacyDesktopDialog
        visible={loadLegacyDialogVisible}
        onHide={() => setLoadLegacyDialogVisible(false)}
        onImport={handleImportLegacyDesktop}
        onImportSilverlight={handleImportSilverlightDesktop}
      />
      <ConversionSummaryDialog
        visible={conversionSummaryVisible}
        onHide={() => setConversionSummaryVisible(false)}
        desktopName={conversionData?.desktopName || ""}
        workspace={conversionData?.workspace}
        stats={conversionData?.stats}
        sourceLabel={conversionData?.sourceLabel}
        itemLabel={conversionData?.itemLabel}
      />
      <SaveTemplateDialog
        visible={saveTemplateDialogVisible}
        onHide={() => setSaveTemplateDialogVisible(false)}
        onSave={handleSaveTemplateRemote}
      />
      <SaveAsTemplateDialog
        visible={saveAsTemplateDialogVisible}
        onHide={() => setSaveAsTemplateDialogVisible(false)}
        onSave={handleSaveAsTemplate}
        onRefresh={listTemplatesRemote}
      />
      <LoadTemplateDialog
        visible={loadTemplateDialogVisible}
        onHide={() => setLoadTemplateDialogVisible(false)}
        onLoad={handleLoadTemplateRemote}
        onDuplicate={duplicateTemplateRemote}
        onRename={renameTemplateRemote}
        onDelete={deleteTemplateRemote}
        onRefresh={listTemplatesRemote}
      />
      <LoadLegacyTemplateDialog
        visible={loadLegacyTemplateDialogVisible}
        onHide={() => setLoadLegacyTemplateDialogVisible(false)}
        onImport={handleImportLegacyTemplate}
        onImportSilverlight={handleImportSilverlightTemplate}
      />

      {/* Password Dialog for File Export/Import */}
      <PasswordDialog
        visible={passwordDialogVisible}
        onHide={() => setPasswordDialogVisible(false)}
        onConfirm={passwordDialogConfig.onConfirm}
        title={passwordDialogConfig.title}
        message={passwordDialogConfig.message}
        mode={passwordDialogConfig.mode}
        passwordRequired={passwordDialogConfig.passwordRequired}
      />
    </div>
  );
};

export default WorkspaceMenus;
